import React, { useState } from 'react';
import { submitContact } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, MapPin, Phone, Clock, Send, Target, Users, Trophy } from 'lucide-react';

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(formData);
      toast.success('Nachricht erfolgreich gesendet!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Nachricht konnte nicht gesendet werden');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            Über uns & <span className="text-primary">Kontakt</span>
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Lerne unseren Verein kennen und nimm Kontakt mit uns auf!
          </p>
        </div>
      </section>

      {/* Über uns Section */}
      <section className="section-padding" data-testid="about-section">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-heading font-bold uppercase tracking-tighter mb-6">
                Unsere <span className="text-primary">Geschichte</span>
              </h2>
              <div className="space-y-4 text-text-secondary">
                <p>
                  House of Darts wurde aus der Leidenschaft für den Dartsport gegründet. 
                  Was als kleine Gruppe von Dart-Enthusiasten begann, hat sich zu einem 
                  lebendigen Verein entwickelt, der Spieler aller Erfahrungsstufen willkommen heißt.
                </p>
                <p>
                  Unser Ziel ist es, eine einladende Gemeinschaft zu schaffen, in der jeder 
                  seine Fähigkeiten verbessern und den Sport genießen kann. Von Anfängern 
                  bis hin zu erfahrenen Spielern - bei uns findet jeder seinen Platz.
                </p>
                <p>
                  Mit regelmäßigen Trainings, spannenden Turnieren und einer tollen 
                  Gemeinschaft bieten wir alles, was das Dartherz begehrt.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="card-surface">
                <CardContent className="p-6 text-center">
                  <Target className="w-10 h-10 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-heading font-bold text-primary">180</p>
                  <p className="text-text-secondary text-sm">Perfekte Würfe</p>
                </CardContent>
              </Card>
              <Card className="card-surface">
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 text-accent mx-auto mb-3" />
                  <p className="text-3xl font-heading font-bold text-accent">50+</p>
                  <p className="text-text-secondary text-sm">Aktive Mitglieder</p>
                </CardContent>
              </Card>
              <Card className="card-surface">
                <CardContent className="p-6 text-center">
                  <Trophy className="w-10 h-10 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-heading font-bold text-primary">8</p>
                  <p className="text-text-secondary text-sm">Mannschaften</p>
                </CardContent>
              </Card>
              <Card className="card-surface">
                <CardContent className="p-6 text-center">
                  <Target className="w-10 h-10 text-accent mx-auto mb-3" />
                  <p className="text-3xl font-heading font-bold text-accent">Triple</p>
                  <p className="text-text-secondary text-sm">20 Mentalität</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section-padding bg-surface" data-testid="vision-section">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-heading font-bold uppercase tracking-tighter mb-6">
              Unsere <span className="text-accent">Vision</span>
            </h2>
            <p className="text-text-secondary text-lg">
              Wir wollen den Dartsport in unserer Region fördern und eine Plattform bieten, 
              auf der Spieler zusammenkommen, sich verbessern und Freundschaften schließen können. 
              Ob Hobby-Spieler oder ambitionierter Wettkämpfer - bei House of Darts bist du richtig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-bold uppercase mb-3">Präzision</h3>
                <p className="text-text-secondary text-sm">
                  Wir streben nach Perfektion in jedem Wurf und fördern kontinuierliche Verbesserung.
                </p>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold uppercase mb-3">Gemeinschaft</h3>
                <p className="text-text-secondary text-sm">
                  Eine starke Gemeinschaft ist das Fundament unseres Vereins. Zusammen sind wir stark.
                </p>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-bold uppercase mb-3">Fairplay</h3>
                <p className="text-text-secondary text-sm">
                  Respekt und Fairness stehen bei uns an erster Stelle - auf und neben dem Board.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding" data-testid="contact-form-section">
        <div className="container-max">
          <h2 className="text-3xl font-heading font-bold uppercase tracking-tighter mb-12 text-center">
            Schreib <span className="text-primary">uns</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="card-elevated">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold uppercase tracking-tighter mb-6">
                  Nachricht senden
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        className="input-dark"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        type="email"
                        className="input-dark"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Betreff *</Label>
                    <Input
                      id="subject"
                      className="input-dark"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht *</Label>
                    <Textarea
                      id="message"
                      className="input-dark min-h-[150px]"
                      placeholder="Deine Nachricht an uns..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="btn-primary w-full"
                    data-testid="contact-submit-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Wird gesendet...' : 'Nachricht senden'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-2xl font-heading font-bold uppercase tracking-tighter mb-6">
                Kontaktdaten
              </h3>
              
              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold uppercase mb-1">Adresse</h4>
                      <p className="text-text-secondary">
                        House of Darts<br />
                        Marktplatz 11<br />
                        5204 Straßwalchen<br />
                        Österreich
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold uppercase mb-1">E-Mail</h4>
                      <a href="mailto:house.of.darts.strasswalchen@gmail.com" className="text-accent hover:underline">
                        house.of.darts.strasswalchen@gmail.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold uppercase mb-1">Telefon</h4>
                      <a href="tel:+4367763139171" className="text-text-secondary hover:text-white">
                        0677 63139171
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold uppercase mb-1">Vereinszeiten</h4>
                      <p className="text-text-secondary">
                        Di – Sa: 18:00 – 00:00 Uhr<br />
                        So – Mo: Geschlossen
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
